/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import { AssetType, MediaImageType } from "@nf/common";
import { ROUTES, SEARCH_SORT } from "@nf/constants";
import { GetServerSideProps } from "next";
import PersonScreen, { PersonScreenProps } from "screens/PersonScreen";

export const getServerSideProps: GetServerSideProps<PersonScreenProps> = async (
  ctx
) => {
  const setCacheHeader = await import("helpers/cacheHeaderHelper").then(
    (mod) => mod.setCacheHeader
  );

  const { getCommonServerSideProps } = await import(
    "helpers/getCommonServerSideProps"
  );
  const { commonProps, DataProvider } = await getCommonServerSideProps(ctx);

  setCacheHeader(ctx.res);

  const personId = Array.isArray(ctx.params?.id)
    ? ctx.params?.id[0]
    : ctx.params?.id;

  if (!personId) {
    return {
      redirect: {
        destination: `/${commonProps.country}/${commonProps.language}${ROUTES.PAGE_NOT_FOUND}`,
        permanent: false,
      },
    };
  }

  try {
    const personMediaList = await DataProvider.getMediaList({
      MediaListId: personId,
      PageNumber: 1,
      PageSize: 100,
      QueryParams: {
        assetTypes: [AssetType.Movie, AssetType.Series].join(","),
        participant: personId,
        sort: SEARCH_SORT,
      },
      MediaMapperOptions: {
        ExcludeYear: true,
        ExcludeArticle: true,
        ExcludeLongDescription: true,
        ExcludeExtendedDescription: true,
        ExcludeProducts: false,
        ExcludeSeasons: true,
        ExcludeSubtitles: true,
        ExcludeContentAdvisory: true,
        ExcludeMedia: true,
        ExcludeImagesFromCategories: true,
        ExcludeArticleFromCategories: true,
        ExcludeDescriptionFromCategories: true,
        ExcludeImageType: [
          MediaImageType.Hero_Frontpage,
          MediaImageType.Hero_Asset_Details,
          MediaImageType.Highlight_Asset_Frontpage,
          MediaImageType.Highlight_Asset_Details,
          MediaImageType.Background,
          MediaImageType.Square,
          MediaImageType.Highlights,
          MediaImageType.Promo,
          MediaImageType.Cover,
          MediaImageType.Logo,
        ],
      },
    });

    // No endpoint to get more detailed information about person
    // so we have to do it this way
    const person = personMediaList?.Entities?.[0]?.People?.find(
      (person) => person.PersonId === personId
    );

    if (!person) {
      return {
        redirect: {
          destination: `/${commonProps.country}/${commonProps.language}${ROUTES.PAGE_NOT_FOUND}`,
          permanent: false,
        },
      };
    }

    return {
      props: {
        ...commonProps,
        person,
        personMediaList: personMediaList.Entities,
      },
    };
  } catch {}

  return {
    props: {
      ...commonProps,
      person: undefined,
      personMediaList: [],
    },
  };
};

export default PersonScreen;
